<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :message-filter.sync="messageFilter"
          :message-options="messageOptions"
          :user-filter.sync="userFilter"
          :user-options="userOptions"
          :tenant-options="tenantOptions"
          :tenant-filter.sync="tenantFilter"
          :action-filter.sync="actionFilter"
          :action-options="actionOptions"
          :end-date.sync="endDate"
          :level-filter.sync="levelFilter"
          :level-options="levelOptions"
          :start-date.sync="startDate"
          :resource-filter.sync="resourseFilter"
          :resource-options.sync="resourseOptions"
        />
      </template>
    </filter-modal>

    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />

    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search') + '...'"
                @keypress.enter="refetchData"
              />
              <b-button
                variant="primary"
                class="text-capitalize"
                type="button"
                :title="$t('actions.refresh')"
                @click="refetchData"
              >
                <feather-icon icon="RefreshCwIcon" size="15" />
              </b-button>
              <b-button
                @click="exportExcel()"
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Loading state: this view queries a slow microservice -->
        <template #table-busy>
          <!-- No color variant: `text-primary` is nearly invisible on the dark
               layout, so both the spinner and the label inherit the table's
               text color, which contrasts in either theme -->
          <div class="text-center my-3">
            <b-spinner style="width: 2.5rem; height: 2.5rem; border-width: 0.25em" />
            <div class="mt-1" style="font-size: 1.05rem; font-weight: 700">
              {{ $t("log_microservice.loading") }}
            </div>
          </div>
        </template>

        <!-- Empty result or failed request -->
        <template #empty>
          <div v-if="loadError" class="text-center my-2">
            <feather-icon icon="AlertTriangleIcon" size="24" class="text-danger" />
            <p class="mt-1 mb-25 font-weight-bold text-danger">
              {{ $t("log_microservice.load_error") }}
            </p>
            <p class="mb-0 text-muted small">
              {{ $t("log_microservice.load_error_hint") }}
            </p>
          </div>
          <div v-else class="text-center my-2 text-muted">
            <feather-icon icon="InboxIcon" size="24" />
            <p class="mt-1 mb-0">{{ $t("no_record_found") }}</p>
          </div>
        </template>

        <template #head()="data">
          <span> {{ $t(data.label) }}</span>
        </template>

        <template #cell(diff)="data">
          <div style="max-height: 200px; overflow-y: auto; max-width: 400px">
            <Marked :jsonObject="data.value" :change.sync="change" :key="change" />
          </div>
        </template>

        <template #cell(path)="data">
          <div
            :title="data.item.path"
            style="
              max-width: 150px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            "
          >
            <span>{{ data.item.path }}</span>
          </div>
        </template>

        <template #cell(description)="data">
          <div style="max-width: 150px">
            <span>{{ data.item.description }}</span>
          </div>
        </template>

        <template #cell(level)="data">
          <span>{{ getLevelText(data.value) }}</span>
        </template>

        <template #cell(tenantCat)="data">
          <div style="max-height: 200px; overflow-y: auto; max-width: 400px">
            <span>{{ data.value.map((tenant) => tenant.name).join(", ") }}</span>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import Marked from "./Marked.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import Pagination from "@/components/Pagination.vue";
import Filters from "./Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

export default {
  components: {
    Filters,
    Marked,
    Pagination,
    BreadcrumbMenu,
    ToastificationLoadingContent,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    vSelect,
    FilterModal,
    ButtonFilters,
  },

  setup(_, context) {
    const getName = (str) => {
      try {
        const obj = JSON.parse(str);
        if (Array.isArray(obj)) {
          let format = JSON.stringify(obj.map((elem) => elem.name))
            .replace(/["\[\]]/g, "")
            .replace(/_/g, " ");
          return format;
        } else {
          console.error("The property value is not an array in the JSON");
          return [];
        }
      } catch (error) {
        console.error("Error parsing JSON string:", error);
        return [];
      }
    };

    const getLevelText = (data) => {
      switch (data) {
        case 0:
          return "EMERGENCY";
        case 1:
          return "ALERT";
        case 2:
          return "CRITICAL";
        case 3:
          return "ERROR";
        case 4:
          return "WARNING";
        case 5:
          return "NOTICE";
        case 6:
          return "INFO";
        case 7:
          return "DEBUG";
      }
    };
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const toast = useToast();
    const {
      resourseOptions,
      resourseFilter,
      startDate,
      endDate,
      levelOptions,
      levelFilter,
      actionFilter,
      actionOptions,
      tenantOptions,
      tenantFilter,
      messageOptions,
      messageFilter,
      change,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      loadError,
      formatDateAssigned,
      userOptions,
      userFilter,
      activeFilters,
      loadingToast,
      buttonExport,
      exportExcel,
      clearSearchFilters,
      filtersCount,
      buildFilters,
    } = useList();

    return {
      resourseOptions,
      resourseFilter,
      startDate,
      endDate,
      getName,
      getLevelText,
      levelOptions,
      levelFilter,
      actionFilter,
      actionOptions,
      tenantOptions,
      tenantFilter,
      userOptions,
      userFilter,
      messageOptions,
      messageFilter,
      change,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      loadingToast,
      refetchData,
      loadError,
      formatDateAssigned,
      buttonExport,
      exportExcel,
      clearSearchFilters,
      filtersCount,
      buildFilters,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.per-page-selector {
  width: 90px;
}
</style>
